import { EDITORIAL_CONSOLE_ID, EDITORIAL_ROLES, GRANTABLE_ROLES } from '@buakei/auth';
import type { AdminWorkspace, AuditEvent, Role, WorkspaceKind } from '@buakei/types';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  ErrorState,
  LoadingState,
  PermissionDeniedState,
  cn,
} from '@buakei/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldOff } from 'lucide-react';
import { useId, useState, type FormEvent, type ReactNode } from 'react';
import type { IdentityClient } from '@/auth/identity-client';
import { useAuth } from '@/auth/session';
import { adminApi } from '@/services/api';
import { Page } from '@/shell/Page';

/**
 * Platform access administration (M2-02, ADR-008). The navigation and this screen are shown
 * only to platform administrators for convenience; the Worker re-checks every request.
 */

const ROLE_LABEL: Record<Role, string> = {
  lawyer: 'Lawyer',
  firm_admin: 'Firm administrator',
  student: 'Student',
  legal_editor: 'Legal editor',
  legal_reviewer: 'Legal reviewer',
  support_agent: 'Support agent',
  platform_admin: 'Platform administrator',
};

const KIND_LABEL: Record<WorkspaceKind, string> = {
  personal: 'Personal practice',
  firm: 'Firm',
  learning: 'Learning',
};

const ACTION_LABEL: Record<AuditEvent['action'], string> = {
  'organization.create': 'Organisation created',
  'workspace.create': 'Workspace created',
  'membership.grant': 'Access granted',
  'membership.revoke': 'Access revoked',
};

/** Who access can be granted to: a workspace, or the Editorial console (ADR-023). */
type GrantTarget = { id: string; name: string; description: string; grantable: readonly Role[] };

const EDITORIAL_TARGET: GrantTarget = {
  id: EDITORIAL_CONSOLE_ID,
  name: 'Editorial console',
  description: 'Reviews the public legal corpus; no workspace, firm or client data',
  grantable: EDITORIAL_ROLES,
};

const workspaceTarget = (w: AdminWorkspace): GrantTarget => ({
  id: w.id,
  name: w.name,
  description: `${KIND_LABEL[w.kind]} workspace`,
  grantable: GRANTABLE_ROLES[w.kind],
});

const inputClass =
  'h-10 w-full min-w-0 rounded-md border border-line-200 bg-white px-3 text-ink-900 placeholder:text-muted-600';
const labelClass = 'text-sm font-medium text-ink-900';

const keys = {
  directory: ['admin', 'directory'] as const,
  audit: ['admin', 'audit'] as const,
  members: (id: string) => ['admin', 'members', id] as const,
};

function FormError({ error }: { error: Error | null }) {
  return error ? (
    <p role="alert" className="text-sm text-danger-700">
      {error.message}
    </p>
  ) : null;
}

export function AccessAdminPage() {
  const { session, accounts } = useAuth();
  const isPlatformAdmin =
    session?.provider === 'firebase' &&
    session.memberships.some((m) => m.roles.includes('platform_admin'));

  if (!isPlatformAdmin || !accounts) {
    return (
      <Page title="Access">
        <PermissionDeniedState title="Access management is for platform administrators">
          <p>
            {session?.provider === 'demo'
              ? 'DEMO personas cannot manage real accounts.'
              : 'Ask a platform administrator if you need to grant access.'}
          </p>
        </PermissionDeniedState>
      </Page>
    );
  }
  return <AccessAdmin tokens={accounts} currentUid={session.uid} />;
}

function AccessAdmin({ tokens, currentUid }: { tokens: IdentityClient; currentUid: string }) {
  const directory = useQuery({
    queryKey: keys.directory,
    queryFn: () => adminApi.directory(tokens),
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const workspaces = directory.data?.workspaces ?? [];
  const selectedWorkspace = workspaces.find((w) => w.id === selectedId);
  const selected: GrantTarget | null =
    selectedId === EDITORIAL_CONSOLE_ID
      ? EDITORIAL_TARGET
      : selectedWorkspace || workspaces[0]
        ? workspaceTarget((selectedWorkspace ?? workspaces[0])!)
        : null;

  return (
    <Page
      title="Access"
      description="Create organisations and workspaces, and decide who can use them. Every change is recorded."
      width="wide"
    >
      <p className="flex max-w-3xl items-start gap-2 text-sm text-ink-900">
        <ShieldOff className="mt-0.5 size-4 shrink-0" aria-hidden />
        Granting workspace access never grants access to client matters. Matter access is always a
        separate, explicit grant.
      </p>

      {directory.isPending ? <LoadingState label="Loading organisations and workspaces" /> : null}
      {directory.isError ? (
        <ErrorState
          title="Access records could not be loaded"
          onRetry={() => void directory.refetch()}
        >
          {directory.error.message}
        </ErrorState>
      ) : null}

      {directory.data ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
          <div className="flex flex-col gap-6">
            <WorkspaceList
              workspaces={workspaces}
              organizations={directory.data.organizations}
              selectedId={selected?.id ?? null}
              onSelect={setSelectedId}
            />
            <CreateOrganizationForm tokens={tokens} />
            <CreateWorkspaceForm
              tokens={tokens}
              organizations={directory.data.organizations}
              onCreated={setSelectedId}
            />
          </div>
          <div className="flex flex-col gap-6">
            {selected ? (
              <MembersPanel key={selected.id} tokens={tokens} target={selected} />
            ) : (
              <EmptyState title="No workspaces yet">
                Create an organisation, then a workspace, to start granting access.
              </EmptyState>
            )}
            <RecentChanges tokens={tokens} currentUid={currentUid} />
          </div>
        </div>
      ) : null}
    </Page>
  );
}

function WorkspaceList({
  workspaces,
  organizations,
  selectedId,
  onSelect,
}: {
  workspaces: AdminWorkspace[];
  organizations: { id: string; name: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const orgName = new Map(organizations.map((o) => [o.id, o.name]));
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Workspaces</CardTitle>
        <CardDescription>
          {workspaces.length} workspace{workspaces.length === 1 ? '' : 's'} in{' '}
          {organizations.length} organisation{organizations.length === 1 ? '' : 's'}.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <button
          type="button"
          aria-current={selectedId === EDITORIAL_CONSOLE_ID ? 'true' : undefined}
          onClick={() => onSelect(EDITORIAL_CONSOLE_ID)}
          className={cn(
            'flex w-full flex-col items-start rounded-md border px-3 py-2 text-left',
            selectedId === EDITORIAL_CONSOLE_ID
              ? 'border-navy-900 bg-navy-50'
              : 'border-line-200 hover:bg-navy-50/50',
          )}
        >
          <span className="font-medium text-navy-900">{EDITORIAL_TARGET.name}</span>
          <span
            className={cn(
              'text-xs',
              selectedId === EDITORIAL_CONSOLE_ID ? 'text-navy-800' : 'text-muted-600',
            )}
          >
            Legal editors and reviewers
          </span>
        </button>
        {workspaces.length === 0 ? (
          <p className="text-sm text-muted-600">None yet.</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {workspaces.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  aria-current={w.id === selectedId ? 'true' : undefined}
                  onClick={() => onSelect(w.id)}
                  className={cn(
                    'flex w-full flex-col items-start rounded-md border px-3 py-2 text-left',
                    w.id === selectedId
                      ? 'border-navy-900 bg-navy-50'
                      : 'border-transparent hover:border-line-200 hover:bg-navy-50/50',
                  )}
                >
                  <span className="font-medium text-navy-900">{w.name}</span>
                  <span
                    className={cn(
                      'text-xs',
                      // Muted grey fails AA contrast on the selected navy-50 background.
                      w.id === selectedId ? 'text-navy-800' : 'text-muted-600',
                    )}
                  >
                    {KIND_LABEL[w.kind]}
                    {w.organizationId
                      ? ` · ${orgName.get(w.organizationId) ?? 'Organisation'}`
                      : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function FormCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function CreateOrganizationForm({ tokens }: { tokens: IdentityClient }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const nameId = useId();
  const create = useMutation({
    mutationFn: () => adminApi.createOrganization(tokens, { name: name.trim() }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
    },
  });
  return (
    <FormCard
      title="New organisation"
      description="A firm or institution. Its data stays separate."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={nameId} className={labelClass}>
            Organisation name
          </label>
          <input
            id={nameId}
            required
            minLength={2}
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </div>
        <Button type="submit" disabled={create.isPending} className="self-start">
          {create.isPending ? 'Creating…' : 'Create organisation'}
        </Button>
        <FormError error={create.error} />
      </form>
    </FormCard>
  );
}

function CreateWorkspaceForm({
  tokens,
  organizations,
  onCreated,
}: {
  tokens: IdentityClient;
  organizations: { id: string; name: string }[];
  onCreated: (id: string) => void;
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<WorkspaceKind>('firm');
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const ids = { kind: useId(), name: useId(), org: useId() };
  const create = useMutation({
    mutationFn: () =>
      adminApi.createWorkspace(tokens, {
        kind,
        name: name.trim(),
        ...(kind !== 'personal' && organizationId ? { organizationId } : {}),
      }),
    onSuccess: (workspace) => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['admin'] });
      onCreated(workspace.id);
    },
  });
  return (
    <FormCard
      title="New workspace"
      description="Firm workspaces belong to an organisation. Personal workspaces have one member."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.kind} className={labelClass}>
            Type
          </label>
          <select
            id={ids.kind}
            value={kind}
            onChange={(e) => setKind(e.target.value as WorkspaceKind)}
            className={inputClass}
          >
            {(Object.keys(KIND_LABEL) as WorkspaceKind[]).map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor={ids.name} className={labelClass}>
            Workspace name
          </label>
          <input
            id={ids.name}
            required
            minLength={2}
            maxLength={120}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </div>
        {kind !== 'personal' ? (
          <div className="flex flex-col gap-1">
            <label htmlFor={ids.org} className={labelClass}>
              Organisation{kind === 'learning' ? ' (optional)' : ''}
            </label>
            <select
              id={ids.org}
              required={kind === 'firm'}
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              className={inputClass}
            >
              <option value="">{kind === 'firm' ? 'Choose an organisation' : 'None'}</option>
              {organizations.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <Button type="submit" disabled={create.isPending} className="self-start">
          {create.isPending ? 'Creating…' : 'Create workspace'}
        </Button>
        <FormError error={create.error} />
      </form>
    </FormCard>
  );
}

function MembersPanel({ tokens, target }: { tokens: IdentityClient; target: GrantTarget }) {
  const queryClient = useQueryClient();
  const members = useQuery({
    queryKey: keys.members(target.id),
    queryFn: () => adminApi.members(tokens, target.id),
  });
  const grantable = target.grantable;
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<Role[]>([grantable[0]!]);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const emailId = useId();

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.members(target.id) });
    void queryClient.invalidateQueries({ queryKey: keys.audit });
  };
  const grant = useMutation({
    mutationFn: () => adminApi.grant(tokens, target.id, { email: email.trim(), roles }),
    onSuccess: () => {
      setMessage(`Access granted to ${email.trim()}.`);
      setEmail('');
      refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: (uid: string) => adminApi.revoke(tokens, target.id, uid),
    onSuccess: () => {
      setConfirming(null);
      setMessage('Access revoked.');
      refresh();
    },
  });

  const active = members.data?.filter((m) => m.status === 'active') ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">{target.name}</CardTitle>
        <CardDescription>
          {target.description} · {active.length} active member
          {active.length === 1 ? '' : 's'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <form
          className="flex flex-col gap-3"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setMessage(null);
            grant.mutate();
          }}
        >
          <h3 className="font-medium text-navy-900">Grant access</h3>
          <div className="flex flex-col gap-1">
            <label htmlFor={emailId} className={labelClass}>
              Email address
            </label>
            <input
              id={emailId}
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
            />
            <p className="text-sm text-muted-600">
              The person must have signed in once and verified their email.
            </p>
          </div>
          <fieldset className="flex flex-col gap-2">
            <legend className={cn(labelClass, 'mb-1')}>Role</legend>
            {grantable.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm text-ink-900">
                <input
                  type="checkbox"
                  className="size-4 accent-navy-900"
                  checked={roles.includes(role)}
                  onChange={(e) =>
                    setRoles((current) =>
                      e.target.checked ? [...current, role] : current.filter((r) => r !== role),
                    )
                  }
                />
                {ROLE_LABEL[role]}
              </label>
            ))}
          </fieldset>
          <Button
            type="submit"
            disabled={grant.isPending || roles.length === 0}
            className="self-start"
          >
            {grant.isPending ? 'Granting…' : 'Grant access'}
          </Button>
          <FormError error={grant.error} />
        </form>
        <p role="status" aria-live="polite" className="text-sm text-ink-900">
          {message}
        </p>

        <section aria-labelledby={`${target.id}-members`} className="flex flex-col gap-2">
          <h3 id={`${target.id}-members`} className="font-medium text-navy-900">
            Members
          </h3>
          {members.isPending ? <LoadingState label="Loading members" /> : null}
          {members.isError ? (
            <ErrorState title="Members could not be loaded" onRetry={() => void members.refetch()}>
              {members.error.message}
            </ErrorState>
          ) : null}
          {members.data && members.data.length === 0 ? (
            <p className="text-sm text-muted-600">Nobody has access yet.</p>
          ) : null}
          {members.data && members.data.length > 0 ? (
            <ul className="divide-y divide-line-200 rounded-md border border-line-200">
              {members.data.map((m) => (
                <li
                  key={m.uid}
                  className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="font-medium break-all text-navy-900">
                      {m.email ?? m.displayName ?? m.uid}
                    </span>
                    <span className="text-sm text-muted-600">
                      {m.roles.map((r) => ROLE_LABEL[r]).join(', ')}
                    </span>
                  </div>
                  {m.status === 'revoked' ? (
                    <Badge>Revoked</Badge>
                  ) : confirming === m.uid ? (
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        disabled={revoke.isPending}
                        onClick={() => revoke.mutate(m.uid)}
                      >
                        Confirm revoke
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setConfirming(null)}>
                        Keep access
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      aria-label={`Revoke access for ${m.email ?? m.uid}`}
                      onClick={() => setConfirming(m.uid)}
                    >
                      Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          <FormError error={revoke.error} />
        </section>
      </CardContent>
    </Card>
  );
}

function RecentChanges({ tokens, currentUid }: { tokens: IdentityClient; currentUid: string }) {
  const audit = useQuery({ queryKey: keys.audit, queryFn: () => adminApi.audit(tokens) });
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Recent changes</CardTitle>
        <CardDescription>The latest access changes, newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {audit.isPending ? <LoadingState label="Loading recent changes" /> : null}
        {audit.isError ? (
          <ErrorState title="Changes could not be loaded" onRetry={() => void audit.refetch()}>
            {audit.error.message}
          </ErrorState>
        ) : null}
        {audit.data && audit.data.length === 0 ? (
          <p className="text-sm text-muted-600">No changes yet.</p>
        ) : null}
        {audit.data && audit.data.length > 0 ? (
          <ol className="flex flex-col gap-2 text-sm">
            {audit.data.map((e) => (
              <li key={e.id} className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
                <time dateTime={e.at} className="shrink-0 text-muted-600 tabular-nums">
                  {new Date(e.at).toLocaleString()}
                </time>
                <span className="text-ink-900">
                  {ACTION_LABEL[e.action]}
                  {e.actorUid === currentUid ? ' by you' : ''}
                </span>
              </li>
            ))}
          </ol>
        ) : null}
      </CardContent>
    </Card>
  );
}
